import React, { FC } from 'react';
import { ScrollView, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import { ProductionOrderContentProps } from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderContent';
import { ALL_JOBS_ID, INFO_SCREEN_LIST_WIDTH, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { isWeb } from '@helpers';
import Loading from '@components/loading';
import i18n from 'i18n-js';
import { LevelSubScreen } from '@v2/pages/common/enums';
import Selectors from '@styles/selectors';
import { useManageProductionOrderJobsList } from './hooks/useManageProductionOrderJobsList';
import { renderJobListItem } from './helpers';

export type ProductionOrderListJobId = number | typeof ALL_JOBS_ID | undefined;

export interface ProductionOrderJobsListProps extends Pick<ProductionOrderContentProps, 'C' | 'font' | 'scale' | 'loading' | 'productionOrder' | 'bg'> {
  width?: number;
  onListItemPress?: (jobId: ProductionOrderListJobId) => void;
  withProductionOrdersNavigation?: boolean;
  listStyle?: StyleProp<ViewStyle>;
  activeLevelSubScreenName?: LevelSubScreen;
}

export { renderJobListItem };

const ProductionOrderJobsList: FC<ProductionOrderJobsListProps> = ({
  C,
  productionOrder,
  withProductionOrdersNavigation = false,
  loading,
  listStyle,
  activeLevelSubScreenName = LevelSubScreen.GENERAL,
  onListItemPress: onPressFromProps,
  width = INFO_SCREEN_LIST_WIDTH,
  bg = isWeb ? 'Background/Light/System' : 'Background/Light/Panel',
  scale = initialScale,
  font = initialFont,
}) => {
  const {
    productionOrderJobsList,
    productionOrders,
    jobId,
    totalTimeForAllJobs,
    noJobSelected,
    allJobSelected,
    onListItemPress,
    navigation,
  } = useManageProductionOrderJobsList({
    productionOrder,
    activeLevelSubScreenName,
    onListItemPress: onPressFromProps,
  });

  return (
    <View
      style={[
        {
          width: scale(width),
          backgroundColor: C(bg),
          paddingHorizontal: scale(16),
          paddingTop: scale(33),
          borderRightColor: C('Hairline/Light/No trans'),
          borderRightWidth: StyleSheet.hairlineWidth,
          height: '100%',
        },
        listStyle,
      ]}
    >
      {loading && (
        <View style={{ marginTop: scale(40) }}>
          <Loading background="transparent" />
        </View>
      )}
      {!loading && !!productionOrder && (
        <View style={{ flex: 1 }}>
          {/* Optional Production Order Navigation */}
          {withProductionOrdersNavigation && productionOrders.data?.length > 1 && (
            <View style={{ flexDirection: 'row', alignSelf: 'flex-start' }}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ marginBottom: scale(31) }}>
                {productionOrders.data?.map((p) => (
                  <Selectors
                    key={p.productionOrderId}
                    scale={scale}
                    font={font}
                    selected={p?.productionOrderId === productionOrder?.productionOrderId}
                    variant="L"
                    text={p.productionOrderNumber}
                    onPress={() => navigation.setParams({ selectedProductionOrderId: p.productionOrderId, selectedProductionOperationId: undefined })}
                  />
                ))}
              </ScrollView>
            </View>
          )}

          {/* Production Order Item */}
          <View style={{ marginBottom: scale(10) }}>
            {renderJobListItem({
              title: productionOrder.productionOrderNumber,
              duration: null,
              isSelected: noJobSelected,
              onPress: () => onListItemPress(undefined),
              scale,
              C,
              font,
              bg,
              fontType: 'Title 2',
              height: 48,
              testID: TEST_IDS.productionOrderJobsList.orderItem,
              selectedTestID: TEST_IDS.productionOrderJobsList.orderItemSelected,
            })}
          </View>

          {productionOrderJobsList.wasLoaded && (
            <>
              {/* All Jobs Item */}
              {renderJobListItem({
                title: i18n.t('Jobs'),
                duration: totalTimeForAllJobs,
                isSelected: allJobSelected,
                onPress: () => onListItemPress(ALL_JOBS_ID),
                scale,
                C,
                font,
                fontType: 'Title 2',
                bg,
                testID: TEST_IDS.productionOrderJobsList.allJobsItem,
                selectedTestID: TEST_IDS.productionOrderJobsList.allJobsItemSelected,
              })}

              {/* Individual Jobs */}
              <ScrollView showsVerticalScrollIndicator={false}>
                {productionOrderJobsList.data?.map((j) => {
                  const currentJobSelected = +jobId === j.productionOperationId;
                  const scheduledTime = j?.totalTimeScheduledSeconds ?? 0;

                  return (
                    <React.Fragment key={j.productionOperationId}>
                      {renderJobListItem({
                        title: j.operationName || '',
                        duration: scheduledTime,
                        isSelected: currentJobSelected,
                        onPress: () => onListItemPress(j.productionOperationId),
                        scale,
                        C,
                        font,
                        bg,
                        testID: TEST_IDS.productionOrderJobsList.jobItem(j.productionOperationId),
                        selectedTestID: TEST_IDS.productionOrderJobsList.jobItemSelected(j.productionOperationId),
                      })}
                    </React.Fragment>
                  );
                })}
              </ScrollView>
            </>
          )}
        </View>
      )}
    </View>
  );
};

export default ColorWrapper(ProductionOrderJobsList);
